/**
 * useTransactions Hook for Real-time Transaction Status Management
 * Tracks user transactions with real-time Firebase updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  updateDoc,
  getDoc 
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';

// Transaction status constants - Updated to match Firestore values
export const TRANSACTION_STATUS = {
  UPLOADING: 'uploading',
  PROCESSING: 'processing', 
  COMPLETED: 'completed',  // Matches your Firestore value
  ERROR: 'error',
  DOWNLOADING: 'downloading'
};

export const useTransactions = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const unsubscribeRef = useRef(null);
  const isMountedRef = useRef(true);
  const timeoutTimersRef = useRef(new Map()); // Track timeout timers for processing transactions

  // Timeout configuration
  const PROCESSING_TIMEOUT = 3 * 60 * 1000; // 3 minutes timeout for processing

  // Update transaction status - moved here to avoid dependency issues
  const updateTransactionStatus = useCallback(async (transactionId, status, additionalData = {}) => {
    try {
      const transactionRef = doc(db, 'transactions', transactionId);
      await updateDoc(transactionRef, {
        status,
        lastUpdated: new Date(),
        ...additionalData
      });
      return true;
    } catch (err) {
      console.error('Error updating transaction status:', err);
      return false;
    }
  }, []);

  // Handle transaction timeout
  const handleTransactionTimeout = useCallback(async (transactionId, transactionFileName) => {
    console.warn(`🕐 Transaction timeout detected for ${transactionFileName} (ID: ${transactionId})`);

    try {
      await updateTransactionStatus(transactionId, TRANSACTION_STATUS.ERROR, {
        errorMessage: 'Processing timeout - the conversion took too long and was automatically cancelled. Please try again or contact support if this continues.',
        timeoutOccurred: new Date(),
        processingTimedOut: true
      });

      console.log(`✅ Transaction ${transactionId} marked as timed out`);
    } catch (err) {
      console.error(`❌ Failed to update timeout status for transaction ${transactionId}:`, err);
    }

    // Clean up timer reference
    timeoutTimersRef.current.delete(transactionId);
  }, [updateTransactionStatus]);

  // Start timeout timer for a processing transaction
  const startTimeoutTimer = useCallback((transactionId, transactionFileName) => {
    // Clear existing timer if any
    if (timeoutTimersRef.current.has(transactionId)) {
      clearTimeout(timeoutTimersRef.current.get(transactionId));
    }

    // Start new timeout timer
    const timeoutId = setTimeout(() => {
      handleTransactionTimeout(transactionId, transactionFileName);
    }, PROCESSING_TIMEOUT);

    timeoutTimersRef.current.set(transactionId, timeoutId);
    console.log(`⏰ Started ${PROCESSING_TIMEOUT / 1000}s timeout timer for transaction: ${transactionFileName} (ID: ${transactionId})`);
  }, [handleTransactionTimeout, PROCESSING_TIMEOUT]);

  // Clear timeout timer for a transaction
  const clearTimeoutTimer = useCallback((transactionId) => {
    if (timeoutTimersRef.current.has(transactionId)) {
      clearTimeout(timeoutTimersRef.current.get(transactionId));
      timeoutTimersRef.current.delete(transactionId);
      console.log(`🔄 Cleared timeout timer for transaction: ${transactionId}`);
    }
  }, []);

  // Real-time listener for user transactions
  useEffect(() => {
    if (!currentUser) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    try {
      console.log('Setting up transaction listener for user:', currentUser.uid);
      
      // Query user's transactions - SIMPLIFIED (no orderBy to avoid index issues)
      const transactionsRef = collection(db, 'transactions');
      const q = query(
        transactionsRef,
        where('userId', '==', currentUser.uid)
        // Removed orderBy to avoid Firestore index requirement
      );

      // Set up real-time listener
      unsubscribeRef.current = onSnapshot(q,
        (snapshot) => {
          if (!isMountedRef.current) return;

          console.log('Transaction snapshot received, docs:', snapshot.size);

          const transactionList = snapshot.docs.map(doc => {
            const data = doc.data();
            console.log('Transaction data:', doc.id, data.status, data.fileName);

            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date()
            };
          });

          // Handle timeout management for processing transactions
          transactionList.forEach(transaction => {
            if (transaction.status === TRANSACTION_STATUS.PROCESSING) {
              // Start timeout timer for processing transactions
              if (!timeoutTimersRef.current.has(transaction.id)) {
                startTimeoutTimer(transaction.id, transaction.fileName);
              }
            } else {
              // Clear timeout timer for non-processing transactions
              clearTimeoutTimer(transaction.id);
            }
          });

          setTransactions(transactionList);
          setLoading(false);
          setError(null);

          console.log('Transactions updated:', transactionList.length);
        },
        (err) => {
          if (!isMountedRef.current) return;
          console.error('Error fetching transactions:', err);
          setError('Failed to load transaction history');
          setLoading(false);
        }
      );

    } catch (err) {
      console.error('Error setting up transaction listener:', err);
      setError('Failed to initialize transaction tracking');
      setLoading(false);
    }

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // Clear all timeout timers
      timeoutTimersRef.current.forEach((timerId, transactionId) => {
        clearTimeout(timerId);
        console.log(`🧹 Cleaned up timeout timer for transaction: ${transactionId}`);
      });
      timeoutTimersRef.current.clear();
    };
  }, [currentUser, startTimeoutTimer, clearTimeoutTimer]);

  // Get single transaction by ID
  const getTransaction = useCallback(async (transactionId) => {
    try {
      const transactionRef = doc(db, 'transactions', transactionId);
      const transactionDoc = await getDoc(transactionRef);
      
      if (transactionDoc.exists()) {
        return {
          id: transactionDoc.id,
          ...transactionDoc.data(),
          createdAt: transactionDoc.data().createdAt?.toDate() || new Date()
        };
      }
      return null;
    } catch (err) {
      console.error('Error fetching transaction:', err);
      return null;
    }
  }, []);

  // Filter transactions by status
  const getTransactionsByStatus = useCallback((status) => {
    return transactions.filter(transaction => transaction.status === status);
  }, [transactions]);

  // Get recent transactions (last 10) - Sort in memory
  const getRecentTransactions = useCallback((limit = 10) => {
    return transactions
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) // Sort by newest first
      .slice(0, limit);
  }, [transactions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    transactions,
    loading,
    error,
    updateTransactionStatus,
    getTransaction,
    getTransactionsByStatus,
    getRecentTransactions,
    // Helper functions
    isProcessing: transactions.some(t => t.status === TRANSACTION_STATUS.PROCESSING),
    hasCompletedTransactions: transactions.some(t => t.status === TRANSACTION_STATUS.COMPLETED),
    totalTransactions: transactions.length
  };
};